import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  Body2,
  Button,
  CancelCrossIcon,
  Caption1,
  ConfirmDialog,
  Dialog,
  formatHours,
  Header3,
  IconButton,
} from "@activecollab/components";
import moment from "moment";
import PropTypes from "prop-types";
import { SplitDialogTableSkeleton } from "~/components/TaskSplits/SplitDialogTableSkeleton";
import UserNameContainer from "~/components/Users/UserNameContainer";
import { useDeleteTaskSplits } from "~/repository/useTaskSplits";
import { getConfigOptions } from "~/store/actions/actions.configOptions";
import { fetchDaysOff } from "~/store/actions/actions.daysOff";
import { loadUsersWorkweekCapacities } from "~/store/actions/actions.users";
import { getWorkloadAvailabilityRecords } from "~/store/actions/actions.workload";
import { getPersonalizedDateFormat } from "~/store/selectors/selector.configOptions";
import { getIsWorkdaysRange } from "~/store/selectors/selector.daysOff";
import { getJobTypeById } from "~/store/selectors/selector.jobTypes";
import { getTaskById } from "~/store/selectors/selector.tasks";
import { formatDateRange } from "~/utils/date";
import { SplitTableRow } from "./SplitTableRow";
import "../../styles/task_splits.less";
import { useSecurity } from "../Hooks";

export const SplitDialog = ({ splits = [], open = false, onClose = () => null, taskId }) => {
  const { t } = useTranslation();
  const task = useSelector((state) => getTaskById(state, taskId));
  const dateFormat = useSelector(getPersonalizedDateFormat);
  const jobType = useSelector((state) => getJobTypeById(state, task.job_type_id));
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const can = useSecurity();
  const canEdit = can(["edit"], task);

  const start_date = task?.start_on ? moment.unix(task.start_on).utc().format("YYYY-MM-DD") : "";
  const end_date = task?.due_on ? moment.unix(task.due_on).utc().format("YYYY-MM-DD") : "";
  const estimate = task?.estimate ?? 0;
  const workdays = useSelector((state) => getIsWorkdaysRange(state, task?.start_on, task?.due_on));

  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const { mutate: deleteSplits } = useDeleteTaskSplits(taskId);

  const lastWorkday = useMemo(() => {
    const daysArray = Object.keys(workdays);
    if (daysArray.length === 0) {
      return null;
    }

    for (let i = daysArray.length - 1; i >= 0; i--) {
      if (workdays[daysArray[i]]) {
        return moment.utc(daysArray[i]).unix();
      }
    }

    return null;
  }, [workdays]);

  useEffect(() => {
    if (start_date && end_date && estimate && open && splits.length > 0) {
      setLoading(true);
      Promise.all([
        dispatch(
          getWorkloadAvailabilityRecords({
            start_date,
            end_date,
          })
        ),
        dispatch(loadUsersWorkweekCapacities()),
        dispatch(getConfigOptions("time_workdays")),
        dispatch(fetchDaysOff()),
      ])
        .then(() => {
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    } else {
      onClose();
    }
  }, [dispatch, end_date, estimate, onClose, open, splits.length, start_date]);

  const isDisabled = useCallback(
    (split) => {
      if (!canEdit) {
        return true;
      }
      if (split?.disabled) {
        return true;
      }
      if (!lastWorkday) {
        return true;
      }
      if (split.date >= lastWorkday) {
        return true;
      }
      return false;
    },
    [canEdit, lastWorkday]
  );

  const calculateMaxEstimate = useCallback(
    (split) => {
      let max = 0.0;
      splits.forEach((s) => {
        if (s.date >= split.date) {
          max += s.duration;
        }
      });

      return Number(max.toFixed(2));
    },
    [splits]
  );

  const onConfirmReset = useCallback(() => {
    deleteSplits();
    setIsResetDialogOpen(false);
  }, [deleteSplits]);

  return (
    <>
      <Dialog
        className="tw-w-[90%] tw-max-w-[700px]"
        open={open}
        onClose={onClose}
        enableBackgroundClick
      >
        <Dialog.Title
          disableDefaultHeading
          className="tw-flex tw-justify-between tw-items-center"
        >
          <Header3>{t("Estimation Splits")}</Header3>
          <IconButton
            onClick={onClose}
            variant="text gray"
          >
            <CancelCrossIcon />
          </IconButton>
        </Dialog.Title>
        <Dialog.ContentDivider />
        <Dialog.Content className="tw-flex tw-flex-col tw-gap-6">
          <div className="tw-flex tw-flex-row tw-gap-4">
            <div className="tw-flex tw-flex-col tw-gap-2 tw-min-w-[88px]">
              <Caption1
                className="tw-h-6 tw-items-center tw-flex"
                weight="bold"
              >
                {t("Assignee")}
              </Caption1>
              <Caption1
                className="tw-h-6 tw-items-center tw-flex"
                weight="bold"
              >
                {t("Date")}
              </Caption1>
              <Caption1
                className="tw-h-6 tw-items-center tw-flex"
                weight="bold"
              >
                {t("Estimate")}
              </Caption1>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-2 tw-flex-1">
              <Body2 className="tw-h-6 tw-items-center tw-flex">
                {task?.assignee_id ? <UserNameContainer userId={task?.assignee_id} /> : t("Unassigned")}
              </Body2>
              <Body2 className="tw-h-6 tw-items-center tw-flex">
                {formatDateRange(task.start_on, task.due_on, dateFormat)}
              </Body2>
              <Body2 className="tw-h-6 tw-items-center tw-flex">
                {t("{{hours}}h of {{jobType}}", {
                  hours: formatHours(task.estimate, false, true),
                  jobType: jobType?.name,
                })}
              </Body2>
            </div>
          </div>

          {loading ? (
            <SplitDialogTableSkeleton />
          ) : (
            <div className="task-split-table tw-flex tw-flex-col">
              <div className="tw-flex tw-bg-[var(--color-theme-200)] tw-items-center tw-h-8 tw-border tw-border-solid tw-border-[var(--color-theme-300)] tw-rounded-t-[8px]">
                <Body2
                  color="primary"
                  className="tw-flex tw-flex-1 tw-px-3 tw-h-full tw-items-center"
                  weight="medium"
                >
                  {t("Date")}
                </Body2>
                <Body2
                  weight="medium"
                  color="primary"
                  className="tw-h-full tw-flex tw-justify-center tw-items-center tw-px-3 tw-w-[120px] tw-border-0 tw-border-l tw-border-solid tw-border-[var(--color-theme-300)]"
                >
                  {t("Estimated")}
                </Body2>
              </div>
              {splits.map((split, index) => {
                return (
                  <SplitTableRow
                    key={index}
                    estimate={split.duration}
                    userId={task?.assignee_id}
                    date={split.date}
                    taskId={taskId}
                    disabled={isDisabled(split)}
                    pending={split.pending}
                    maxEstimate={calculateMaxEstimate(split)}
                    canEdit={canEdit}
                  />
                );
              })}
            </div>
          )}
        </Dialog.Content>
        <Dialog.Actions>
          {canEdit ? (
            <Button
              variant="tertiary"
              className="tw-w-fit"
              size="small"
              disabled={!canEdit}
              onClick={() => setIsResetDialogOpen(true)}
            >
              {t("Reset Splits")}
            </Button>
          ) : null}
        </Dialog.Actions>
      </Dialog>
      <ConfirmDialog
        dialogTitle={t("Reset Estimation Splits")}
        dialogContent={t(
          "This will reset all estimation splits to their defaults. Any custom values you've set will be lost permanently."
        )}
        open={isResetDialogOpen}
        onConfirm={onConfirmReset}
        confirmBtnText={t("Reset")}
        onCancel={() => setIsResetDialogOpen(false)}
      />
    </>
  );
};

SplitDialog.displayName = "SplitDialog";

SplitDialog.propTypes = {
  splits: PropTypes.array,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  taskId: PropTypes.number.isRequired,
};
